import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, ArrowDownIcon, SparklesIcon, CheckIcon, ShieldCheckIcon } from 'lucide-react';
import { useWebinar } from '../../hooks/useWebinar';
const EASE = [0.23, 1, 0.32, 1] as const;
export function WebinarDemo() {
  const reduce = useReducedMotion();
  const c = useWebinar().copy.demo;
  const demoSteps = c.steps;
  const atnaPrepared = c.prepared;
  return <section id="demonstration" aria-labelledby="demo-title" className="scroll-mt-[72px] bg-[#070A1C] px-5 md:px-8 pb-20 md:pb-28" data-id="element-7864" data-mp="2u8udkmc2aj8s">
      <div className="max-w-[1280px] mx-auto rounded-[28px] border border-[#4F46E5]/30 bg-[#0D0F33] px-6 py-14 md:px-12 md:py-16" data-id="element-7865" data-mp="2peq3vvbevx40">
        <p className="font-sans text-[11.5px] font-semibold tracking-[0.2em] uppercase text-[#A5A1FF]" data-id="element-7866" data-mp="2ggeflzy0pbeq">{c.eyebrow}</p>
        <h2 id="demo-title" className="mt-4 max-w-[40rem] font-serif text-[2rem] md:text-[2.6rem] leading-[1.12] tracking-tight text-white" data-id="element-7867" data-mp="2bfqhkubdfub0">
          {c.title}
        </h2>

        {/* Workflow */}
        <ol className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-3 lg:gap-0" data-id="element-7868" data-mp="2spexz7f3x5ki">
          {demoSteps.map((s, i) => <li key={s.label} className="relative flex lg:block flex-col" data-id="element-7869" data-mp="2xirbalec5pxy" data-mpi={i}>
              <div className={`h-full rounded-2xl border p-5 lg:mr-6 ${i === 0 ? 'border-white/15 bg-white/[0.05]' : 'border-white/[0.08] bg-[#0A0C28]'}`} data-id="element-7870" data-mp="2jqumaq7yrw5s">
                <p className="font-sans text-[11px] font-semibold tracking-[0.16em] uppercase text-[#A5A1FF]" data-id="element-7871" data-mp="3ceh1ywugur90">{s.label}</p>
                <p className="mt-2.5 font-sans text-[14px] leading-snug text-white/90" data-id="element-7872" data-mp="3ggkfdr0p45wx">{s.text}</p>
              </div>
              {i < demoSteps.length - 1 && <>
                  <ArrowRightIcon className="hidden lg:block absolute right-0.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6F7598]" aria-hidden="true" data-id="element-7873" data-mp="37f447h223zrn" />
                  <ArrowDownIcon className="lg:hidden mx-auto mt-3 w-4 h-4 text-[#6F7598]" aria-hidden="true" data-id="element-7874" data-mp="1qzje0snxjdcc" />
                </>}
            </li>)}
        </ol>

        {/* With ATNA */}
        <div className="mt-14 pt-12 border-t border-white/[0.08] grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start" data-id="element-7875" data-mp="1bxb531y2k42r">
          <div data-id="element-7876" data-mp="135srmysic2dd">
            <p className="flex items-center gap-2 font-sans text-[12px] font-semibold tracking-[0.16em] uppercase text-white" data-id="element-7877" data-mp="13597rcjar3wp">
              <SparklesIcon className="w-4 h-4 text-[#A5A1FF]" aria-hidden="true" data-id="element-7878" data-mp="y5wmgw82q4w8" />
              {c.withAtna}
            </p>
            <div className="mt-5 rounded-2xl border border-white/10 bg-[#070A1C] p-5" data-id="element-7879" data-mp="t5idnbyneqx5">
              <p className="font-sans text-[15.5px] leading-relaxed text-white" data-id="element-7880" data-mp="o6ytnh3m7p7f">{c.quote[0]}{c.prompt}{c.quote[1]}</p>
              <div className="mt-4 flex justify-end" data-id="element-7881" data-mp="k913oc20sz2z">
                <span className="inline-flex w-9 h-9 items-center justify-center rounded-full bg-[#4F46E5]" aria-hidden="true" data-id="element-7882" data-mp="f8vf54r1rsp4">
                  <ArrowRightIcon className="w-4 h-4 text-white" data-id="element-7883" data-mp="2ek6atqcbftn2" />
                </span>
              </div>
            </div>
            <p className="mt-5 flex items-start gap-2.5 font-sans text-[13.5px] leading-relaxed text-[#A6ABC8]" data-id="element-7884" data-mp="2h5dre6xlgeng">
              <ShieldCheckIcon className="w-4 h-4 mt-0.5 shrink-0 text-[#A5A1FF]" aria-hidden="true" data-id="element-7885" data-mp="2m478e9c3ansd" />
              {c.governance}
            </p>
          </div>

          <div className="rounded-2xl border border-[#4F46E5]/40 bg-[#131546] p-6" data-id="element-7886" data-mp="2qvyvuazy8dea">
            <p className="font-sans text-[12px] font-semibold tracking-[0.14em] uppercase text-[#C7C4FF]" data-id="element-7887" data-mp="2vtqr9ezsyhn8">{c.preparing}</p>
            <ul className="mt-4 divide-y divide-white/[0.08]" data-id="element-7888" data-mp="1y8m9a0tsg5kz">
              {atnaPrepared.map((item, i) => {
              const last = i === atnaPrepared.length - 1;
              return <motion.li key={item} initial={reduce ? false : {
                opacity: 0,
                y: 6
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-60px'
              }} transition={{
                duration: 0.22,
                delay: i * 0.06,
                ease: EASE
              }} className="flex items-center gap-3 py-3" data-id="element-7889" data-mp="286sa0f3uz5o9" data-mpi={i}>
                    <span className={`inline-flex w-6 h-6 items-center justify-center rounded-full ${last ? 'bg-white text-[#4F46E5]' : 'bg-[#4F46E5] text-white'}`} data-id="element-7890" data-mp="3m63twshm6r6d">
                      <CheckIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-7891" data-mp="3w41ijh3dypzi" />
                    </span>
                    <span className={`font-sans text-[15px] ${last ? 'font-semibold text-white' : 'text-white/90'}`} data-id="element-7892" data-mp="3r22e03vwtn0z">{item}</span>
                  </motion.li>;
            })}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}